<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { Camera, Monitor, PanelsTopLeft, Volume2 } from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import * as Sentry from '@sentry/nuxt'
import { invoke } from '~/lib/tauriInvoke'
import { listen } from '@tauri-apps/api/event'
import { isTauriRuntime } from '~/lib/platform/runtime'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { ScreenShareQuality } from '~/lib/livekit/types'
import { loadAppSettings } from '~/lib/settings/appSettings'

type SourceTab = 'application' | 'screen' | 'camera'
type NativeCaptureSource = {
  id: number
  kind: 'application' | 'screen'
  title: string
  appName?: string
  width: number
  height: number
  preview?: string
}

const props = defineProps<{
  open: boolean
  cameras: MediaDeviceInfo[]
  initialQuality: ScreenShareQuality
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'start', stream: MediaStream, quality: ScreenShareQuality, fps: number, includeAudio: boolean): void
}>()

const tab = ref<SourceTab>('application')
const quality = ref<ScreenShareQuality>(props.initialQuality)
const fps = ref('30')
// Seeded from the last persisted choice (see `LiveKitManager.startScreenShare`, which is what
// actually writes it back) — defaults to off, same as the app-wide setting.
const includeAudio = ref(loadAppSettings().video.screenShareAudioEnabled)
const selectedId = ref<string | null>(null)
const selectedStream = ref<MediaStream | null>(null)
const selectedNativeSource = ref<NativeCaptureSource | null>(null)
const cameraStreams = new Map<string, MediaStream>()
const previewElements = new Map<string, HTMLVideoElement>()
const loadingCameraId = ref<string | null>(null)
const sourcesLoading = ref<Record<'application' | 'screen', boolean>>({
  application: false,
  screen: false,
})
const nativeSources = ref<Record<'application' | 'screen', NativeCaptureSource[]>>({
  application: [],
  screen: [],
})
const sourceLoadGeneration = ref<Record<'application' | 'screen', number>>({
  application: 0,
  screen: 0,
})
const pendingPreviews = new Set<string>()

// Native window/screen capture is a real OS call (GDI/DXGI); running previews
// fully serially made the grid trickle in one thumbnail at a time. A small
// concurrency pool keeps the UI responsive while still loading several at once.
const PREVIEW_CONCURRENCY = 3
let activePreviewCount = 0
const previewWaiters: Array<() => void> = []

function invalidateSourceLoads() {
  sourceLoadGeneration.value = {
    application: sourceLoadGeneration.value.application + 1,
    screen: sourceLoadGeneration.value.screen + 1,
  }
}

async function withPreviewSlot<T>(task: () => Promise<T>): Promise<T> {
  if (activePreviewCount >= PREVIEW_CONCURRENCY) {
    await new Promise<void>(resolve => previewWaiters.push(resolve))
  }
  activePreviewCount++
  try {
    return await task()
  } finally {
    activePreviewCount--
    previewWaiters.shift()?.()
  }
}

const qualities: Array<{ value: ScreenShareQuality; label: string }> = [
  { value: '720p', label: '720p' },
  { value: '1080p', label: '1080p' },
  { value: '1440p', label: '1440p' },
]

const canStart = computed(() => (
  Boolean(selectedNativeSource.value || selectedStream.value?.getVideoTracks().length)
))

function applyPreset(preset: 'text' | 'motion') {
  if (preset === 'text') {
    quality.value = '1440p'
    fps.value = '15'
  } else {
    quality.value = '720p'
    fps.value = '30'
  }
}

function attachPreview(key: string, element: unknown) {
  const video = element as HTMLVideoElement | null
  if (!video) {
    previewElements.delete(key)
    return
  }
  previewElements.set(key, video)
  const stream = key === 'selected' ? selectedStream.value : cameraStreams.get(key)
  if (stream && video.srcObject !== stream) video.srcObject = stream
}

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach(track => track.stop())
}

function clearSelection(stop = true) {
  if (stop && selectedStream.value && !cameraStreams.has(selectedId.value || '')) {
    stopStream(selectedStream.value)
  }
  selectedStream.value = null
  selectedNativeSource.value = null
  selectedId.value = null
}

async function loadCameraPreviews() {
  // Opening every physical camera at once can block WebKit and some drivers.
  // A camera is opened only after the user selects its card.
}

function stopCameraPreviews(exceptId?: string) {
  for (const [id, stream] of cameraStreams) {
    if (id !== exceptId) stopStream(stream)
  }
  if (exceptId) {
    const kept = cameraStreams.get(exceptId)
    cameraStreams.clear()
    if (kept) cameraStreams.set(exceptId, kept)
  } else {
    cameraStreams.clear()
  }
}

function chooseDisplaySource(source: NativeCaptureSource) {
  clearSelection()
  selectedId.value = `${source.kind}-${source.id}`
  selectedNativeSource.value = source
}

const BROWSER_DISPLAY_ID = 'browser-display-media'

// No OS-level capture (`get_capture_sources`/`start_live_capture`) outside Tauri — the browser's
// own `getDisplayMedia()` picker (tab/window/screen chooser is native browser UI, not ours) is
// the only way to share a screen there. Unlike the native path there's no separate low-res
// preview step: the stream this returns already *is* the real capture, so `startSharing()` uses
// it directly instead of re-requesting at "real" quality like it does for cameras.
async function chooseBrowserDisplaySource() {
  clearSelection()
  const width = qualityWidth(quality.value)
  const height = Math.round(width * 9 / 16)
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { width: { ideal: width }, height: { ideal: height }, frameRate: { ideal: Number(fps.value) } },
      audio: includeAudio.value,
    })
    if (!props.open) {
      stopStream(stream)
      return
    }
    selectedId.value = BROWSER_DISPLAY_ID
    selectedStream.value = stream
    stream.getVideoTracks()[0]?.addEventListener('ended', () => {
      if (selectedId.value === BROWSER_DISPLAY_ID) clearSelection()
    }, { once: true })
    await nextTick()
    attachPreview('selected', previewElements.get('selected'))
  } catch (error) {
    // AbortError/NotAllowedError just means the user closed the browser's own picker or denied
    // it — not worth logging as an app error.
    if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return
    console.error('getDisplayMedia failed', error)
    Sentry.captureException(error, { extra: { action: 'get_display_media' } })
    toast.error('Не удалось выбрать окно или экран')
  }
}

async function loadNativeSources(kind: 'application' | 'screen') {
  // Native-only — in browser mode `chooseBrowserDisplaySource` above handles both tabs via the
  // browser's own picker instead, there's no source list to fetch.
  if (!isTauriRuntime()) return
  if (nativeSources.value[kind].length || sourcesLoading.value[kind]) return
  const generation = sourceLoadGeneration.value[kind]
  sourcesLoading.value[kind] = true
  try {
    const sources = await invoke<NativeCaptureSource[]>('get_capture_sources', { kind })
    if (generation !== sourceLoadGeneration.value[kind] || !props.open) return

    // Large application lists are mounted in small batches. Rendering dozens
    // of cards and base64 images in one Vue update visibly freezes WebView.
    nativeSources.value[kind] = sources.slice(0, 12)
    for (let visible = 24; visible < sources.length + 12; visible += 12) {
      await yieldToBrowser()
      if (generation !== sourceLoadGeneration.value[kind] || !props.open) return
      nativeSources.value[kind] = sources.slice(0, visible)
    }
    sourcesLoading.value[kind] = false

    // Only visible leading cards are preloaded at low resolution. Remaining
    // application previews load on hover, avoiding capture of dozens of windows.
    // Both tabs' source lists are prefetched in parallel on open (see the
    // `open` watcher), so this runs in the background even while another tab
    // is active — that's what makes switching tabs feel instant afterwards.
    const previewSources = kind === 'application'
      ? nativeSources.value[kind].slice(0, 4)
      : nativeSources.value[kind]
    await Promise.all(previewSources.map(source =>
      queueSourcePreview(source, kind === 'application' ? 240 : 360, kind, generation),
    ))
  } catch (error) {
    console.error(`Failed to enumerate ${kind} sources`, error)
    Sentry.captureException(error, { extra: { action: 'enumerate_capture_sources', kind } })
    toast.error(kind === 'screen' ? 'Не удалось получить список экранов' : 'Не удалось получить список приложений')
  } finally {
    sourcesLoading.value[kind] = false
  }
}

async function loadSourcePreview(source: NativeCaptureSource) {
  if (source.preview) return
  await queueSourcePreview(source, 480, source.kind, sourceLoadGeneration.value[source.kind])
}

function yieldToBrowser() {
  return new Promise<void>(resolve => {
    requestAnimationFrame(() => setTimeout(resolve, 0))
  })
}

function queueSourcePreview(source: NativeCaptureSource, maxWidth: number, kind: 'application' | 'screen', generation: number) {
  const key = `${source.kind}-${source.id}`
  if (source.preview || pendingPreviews.has(key)) return Promise.resolve()
  pendingPreviews.add(key)
  return withPreviewSlot(async () => {
    try {
      if (generation !== sourceLoadGeneration.value[kind] || !props.open) return
      source.preview = await invoke<string>('capture_source_frame', {
        kind: source.kind,
        id: source.id,
        maxWidth,
      })
    } catch (error) {
      console.warn(`Preview failed for ${source.kind} ${source.id}`, error)
      Sentry.captureException(error, { level: 'warning', extra: { action: 'capture_source_frame', kind: source.kind, id: source.id } })
    } finally {
      pendingPreviews.delete(key)
    }
  })
}

function qualityWidth(value: ScreenShareQuality) {
  if (value === '720p') return 1280
  if (value === '1080p') return 1920
  return 2560
}

function imageFromDataUrl(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = url
  })
}

type SystemAudioFrame = { buffers: string[]; channels: number[] }

function decodeFloatBuffer(base64: string) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index)
  return new Float32Array(bytes.buffer)
}

async function addSystemAudioTrack(stream: MediaStream, source: NativeCaptureSource) {
  // OS-level loopback capture (`start_system_audio_capture`) and the `screen-audio-frame` event
  // it streams over are both Tauri-only — fail clearly here instead of `listen()` throwing on the
  // missing `__TAURI_INTERNALS__` bridge.
  if (!isTauriRuntime()) throw new Error('System audio capture недоступен в браузерной версии')

  const context = new AudioContext({ sampleRate: 48_000 })
  const destination = context.createMediaStreamDestination()
  const processor = context.createScriptProcessor(1024, 0, 2)
  const queue: Array<[Float32Array, Float32Array]> = []
  let queueOffset = 0

  processor.onaudioprocess = event => {
    const outputs = [event.outputBuffer.getChannelData(0), event.outputBuffer.getChannelData(1)]
    let outputOffset = 0
    while (outputOffset < outputs[0].length && queue.length) {
      const current = queue[0]
      const count = Math.min(current[0].length - queueOffset, outputs[0].length - outputOffset)
      outputs[0].set(current[0].subarray(queueOffset, queueOffset + count), outputOffset)
      outputs[1].set(current[1].subarray(queueOffset, queueOffset + count), outputOffset)
      outputOffset += count
      queueOffset += count
      if (queueOffset >= current[0].length) {
        queue.shift()
        queueOffset = 0
      }
    }
  }

  const unlisten = await listen<SystemAudioFrame>('screen-audio-frame', event => {
    const { buffers, channels } = event.payload
    if (!buffers.length) return
    if (buffers.length === 1 && channels[0] >= 2) {
      const interleaved = decodeFloatBuffer(buffers[0])
      const frames = Math.floor(interleaved.length / channels[0])
      const left = new Float32Array(frames)
      const right = new Float32Array(frames)
      for (let index = 0; index < frames; index++) {
        left[index] = interleaved[index * channels[0]]
        right[index] = interleaved[index * channels[0] + 1]
      }
      queue.push([left, right])
    } else {
      const left = decodeFloatBuffer(buffers[0])
      queue.push([left, buffers[1] ? decodeFloatBuffer(buffers[1]) : left])
    }
    if (queue.length > 4) {
      queue.splice(0, queue.length - 4)
      queueOffset = 0
    }
  })

  processor.connect(destination)
  await context.resume()
  try {
    await invoke('start_system_audio_capture', { kind: source.kind, id: source.id })
  } catch (error) {
    unlisten()
    processor.disconnect()
    await context.close()
    throw error
  }

  const audioTrack = destination.stream.getAudioTracks()[0]
  stream.addTrack(audioTrack)
  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    unlisten()
    processor.disconnect()
    void invoke('stop_system_audio_capture').catch(() => undefined)
    void context.close()
  }
  stream.getVideoTracks()[0]?.addEventListener('ended', cleanup, { once: true })
  audioTrack.addEventListener('ended', cleanup, { once: true })
}

async function createSelectedSourceStream(source: NativeCaptureSource, frameRate: number) {
  // Native OS capture (`start_live_capture`) and its `live-capture-frame`/`live-capture-error`
  // events are Tauri-only — this whole source-picker tab is unreachable in browser mode already
  // (see `get_capture_sources`), this is just belt-and-suspenders.
  if (!isTauriRuntime()) throw new Error('Live capture недоступен в браузерной версии')

  const maxWidth = Math.min(source.width, qualityWidth(quality.value))
  const scale = Math.min(1, maxWidth / source.width)
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(2, Math.round(source.width * scale))
  canvas.height = Math.max(2, Math.round(source.height * scale))
  const context = canvas.getContext('2d', { alpha: false })
  if (!context || typeof canvas.captureStream !== 'function') throw new Error('Canvas capture is unavailable')

  // Frames are pushed continuously from a native OS capture (Windows Graphics
  // Capture/DXGI Desktop Duplication, or AVCaptureScreenInput on macOS) running
  // on its own Rust thread — never pulled one screenshot-round-trip at a time.
  // See `start_live_capture`/`stop_live_capture` in screen_capture.rs.
  const unlistenFrame = await listen<{ dataUrl: string; width: number; height: number }>(
    'live-capture-frame',
    async event => {
      const { dataUrl, width, height } = event.payload
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      try {
        const image = await imageFromDataUrl(dataUrl)
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
      } catch {
        // A stale/corrupt frame is not worth surfacing — the next one arrives shortly.
      }
    },
  )
  const unlistenError = await listen<{ message: string }>('live-capture-error', event => {
    console.error('Live capture failed', event.payload.message)
    toast.error(event.payload.message || 'Захват источника прерван')
    // The capture is dead on the Rust side already (it stopped its own loop before emitting
    // this) — ending the track here lets LiveKit unpublish and the UI drop out of "sharing"
    // state instead of silently continuing to publish a frozen/black tile.
    stream.getVideoTracks()[0]?.stop()
  })

  try {
    await invoke('start_live_capture', {
      kind: source.kind,
      id: source.id,
      maxWidth,
      fps: frameRate,
    })
  } catch (error) {
    unlistenFrame()
    unlistenError()
    throw error
  }

  const stream = canvas.captureStream(frameRate)
  await addSystemAudioTrack(stream, source).catch(error => {
    console.warn('Application audio capture is unavailable', error)
    Sentry.captureException(error, { level: 'warning', extra: { action: 'add_system_audio_track' } })
  })

  let stopped = false
  const stopLiveCapture = () => {
    if (stopped) return
    stopped = true
    unlistenFrame()
    unlistenError()
    void invoke('stop_live_capture').catch(() => undefined)
  }
  stream.getVideoTracks()[0]?.addEventListener('ended', stopLiveCapture, { once: true })
  return stream
}

async function chooseCamera(camera: MediaDeviceInfo) {
  if (selectedId.value === camera.deviceId && selectedStream.value) return
  clearSelection()
  stopCameraPreviews()
  selectedId.value = camera.deviceId
  loadingCameraId.value = camera.deviceId
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: camera.deviceId }, width: { ideal: 640 }, height: { ideal: 360 } },
      audio: false,
    })
    if (!props.open || tab.value !== 'camera' || selectedId.value !== camera.deviceId) {
      stopStream(stream)
      return
    }
    cameraStreams.set(camera.deviceId, stream)
    selectedStream.value = stream
    await nextTick()
    attachPreview(camera.deviceId, previewElements.get(camera.deviceId))
  } catch (error) {
    console.error(`Camera preview failed for ${camera.deviceId}`, error)
    Sentry.captureException(error, { extra: { action: 'open_camera_preview', deviceId: camera.deviceId } })
    toast.error('Не удалось открыть камеру')
    clearSelection(false)
  } finally {
    if (loadingCameraId.value === camera.deviceId) loadingCameraId.value = null
  }
}

async function startSharing() {
  if (!selectedStream.value && !selectedNativeSource.value) return
  const nativeSource = selectedNativeSource.value
  const cameraId = tab.value === 'camera' ? selectedId.value : null
  // Captured now, same as `nativeSource`/`cameraId` above — `selectedStream` gets nulled out
  // further down (ownership transfers to LiveKit) before this is used.
  const browserDisplayStream = selectedId.value === BROWSER_DISPLAY_ID ? selectedStream.value : null
  const selectedQuality = quality.value
  const selectedFps = Number(fps.value)
  const withAudio = includeAudio.value

  // Cancel queued preview screenshots before starting a live capture. They are
  // useful only inside the picker and must never compete with WebRTC encoding.
  invalidateSourceLoads()
  pendingPreviews.clear()

  // Close first. Capture and LiveKit publication continue after the closing
  // frame, so expensive native work never blocks the dialog animation.
  emit('update:open', false)
  await nextTick()
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

  try {
    let stream: MediaStream
    if (nativeSource) {
      quality.value = selectedQuality
      stream = await createSelectedSourceStream(nativeSource, selectedFps)
    } else if (browserDisplayStream) {
      stream = browserDisplayStream
    } else {
      const width = qualityWidth(selectedQuality)
      const height = Math.round(width * 9 / 16)
      stream = await navigator.mediaDevices.getUserMedia({
        video: cameraId ? {
          deviceId: { exact: cameraId },
          width: { ideal: width },
          height: { ideal: height },
          frameRate: { ideal: selectedFps, max: selectedFps },
        } : true,
        audio: withAudio,
      })
    }

    emit('start', stream, selectedQuality, selectedFps, withAudio)
    selectedStream.value = null // ownership is transferred to LiveKit
  } catch (error) {
    // The Rust command only ever rejects with a plain string (Tauri IPC errors carry no stack
    // trace) — logging the source's kind/id/title alongside it is what actually makes a Sentry
    // report actionable, since the message text alone is often the same generic fallback.
    console.error('Starting screen share failed', {
      error,
      kind: nativeSource?.kind ?? (cameraId ? 'camera' : 'display-media'),
      sourceId: nativeSource?.id,
      sourceTitle: nativeSource?.title,
    })
    Sentry.captureException(error, {
      extra: {
        action: 'start_screen_share',
        kind: nativeSource?.kind ?? (cameraId ? 'camera' : 'display-media'),
        sourceId: nativeSource?.id,
        sourceTitle: nativeSource?.title,
        quality: selectedQuality,
        fps: selectedFps,
      },
    })
    toast.error('Не удалось начать демонстрацию')
  }
}

function close() {
  invalidateSourceLoads()
  emit('update:open', false)
}

watch(() => props.open, open => {
  if (open) {
    quality.value = props.initialQuality
    nativeSources.value = { application: [], screen: [] }
    sourcesLoading.value = { application: false, screen: false }
    invalidateSourceLoads()
    // Both source lists are prefetched in parallel regardless of the active
    // tab, so switching between "Приложение" and "Весь экран" is instant once
    // the dialog has been open for a moment.
    void loadNativeSources('application')
    void loadNativeSources('screen')
    if (tab.value === 'camera') void loadCameraPreviews()
  } else {
    clearSelection()
    stopCameraPreviews()
  }
})

watch(quality, value => {
  // 1080p is only allowed at exactly 30 FPS for now.
  if (value === '1080p') fps.value = '30'
})

watch(tab, value => {
  clearSelection()
  stopCameraPreviews()
  if (value === 'camera' && props.open) void loadCameraPreviews()
})

onUnmounted(() => {
  invalidateSourceLoads()
  clearSelection()
  stopCameraPreviews()
  for (const video of previewElements.values()) video.srcObject = null
  previewElements.clear()
  pendingPreviews.clear()
  nativeSources.value = { application: [], screen: [] }
})
</script>

<template>
  <Dialog :open="open" @update:open="value => value ? null : close()">
    <DialogContent class="flex h-[90vh] max-h-[1000px] !w-[96vw] !max-w-[1600px] flex-col gap-0 overflow-hidden p-0 sm:!max-w-[1600px]">
      <DialogHeader class="border-b px-6 py-5">
        <DialogTitle class="text-xl">Включить демонстрацию экрана</DialogTitle>
        <DialogDescription>Выберите источник и параметры трансляции</DialogDescription>
      </DialogHeader>

      <Tabs v-model="tab" class="min-h-0 flex-1 gap-0">
        <TabsList class="mx-6 mt-4 grid w-[560px] grid-cols-3">
          <TabsTrigger value="application"><PanelsTopLeft class="size-4" />Приложение</TabsTrigger>
          <TabsTrigger value="screen"><Monitor class="size-4" />Весь экран</TabsTrigger>
          <TabsTrigger value="camera"><Camera class="size-4" />Внешние камеры</TabsTrigger>
        </TabsList>

        <div class="min-h-0 flex-1 overflow-y-auto p-6">
          <TabsContent v-for="kind in ['application', 'screen']" :key="kind" :value="kind" class="mt-0 h-full">
            <!-- Browser mode has no OS-level source enumeration — `getDisplayMedia()`'s own
                 tab/window/screen picker (native browser UI) covers both tabs equally. -->
            <div v-if="!isTauriRuntime()" class="grid h-52 place-items-center gap-3 rounded-xl border border-dashed text-center">
              <template v-if="selectedId === BROWSER_DISPLAY_ID">
                <p class="text-sm text-muted-foreground">Источник выбран</p>
                <Button variant="outline" size="sm" @click="chooseBrowserDisplaySource">Выбрать другой</Button>
              </template>
              <template v-else>
                <p class="max-w-xs text-sm text-balance text-muted-foreground">
                  Выбор {{ kind === 'application' ? 'окна' : 'экрана' }} происходит через системное окно браузера
                </p>
                <Button size="sm" @click="chooseBrowserDisplaySource">Выбрать {{ kind === 'application' ? 'окно' : 'экран' }}</Button>
              </template>
            </div>
            <div v-else class="grid grid-cols-2 gap-4">
              <template v-if="sourcesLoading[kind as 'application' | 'screen'] && nativeSources[kind as 'application' | 'screen'].length === 0">
                <div v-for="n in 8" :key="n" class="aspect-video overflow-hidden rounded-xl border-2 border-border">
                  <Skeleton class="size-full rounded-none" />
                </div>
              </template>
              <button
                v-for="source in nativeSources[kind as 'application' | 'screen']"
                :key="`${source.kind}-${source.id}`"
                class="group relative aspect-video overflow-hidden rounded-xl border-2 bg-black transition-colors"
                :class="selectedId === `${source.kind}-${source.id}` ? 'border-primary' : 'border-border hover:border-primary/60'"
                @click="chooseDisplaySource(source)"
                @pointerenter="loadSourcePreview(source)"
              >
                <img v-if="source.preview" :src="source.preview" class="size-full object-contain" :alt="source.title">
                <div v-else class="relative size-full">
                  <Skeleton class="absolute inset-0 rounded-none" />
                  <div class="relative grid size-full place-items-center text-muted-foreground/50">
                    <PanelsTopLeft v-if="kind === 'application'" class="size-10" />
                    <Monitor v-else class="size-10" />
                  </div>
                </div>
                <span class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 px-3 pb-2 pt-10 text-left text-white">
                  <span class="block truncate text-sm font-medium">{{ source.title }}</span>
                  <span class="block text-xs text-white/65">{{ source.appName || `${source.width} × ${source.height}` }}</span>
                </span>
              </button>
              <div v-if="!sourcesLoading[kind as 'application' | 'screen'] && nativeSources[kind as 'application' | 'screen'].length === 0" class="col-span-2 grid h-40 place-items-center rounded-xl border border-dashed text-muted-foreground">
                Источники не найдены или доступ к записи экрана не предоставлен
              </div>
            </div>
            <div v-if="selectedStream" class="pointer-events-none fixed bottom-24 right-8 z-[60] aspect-video w-80 overflow-hidden rounded-xl border-2 border-primary bg-black shadow-2xl">
              <video :ref="el => attachPreview('selected', el)" class="size-full object-contain" autoplay muted playsinline />
            </div>
          </TabsContent>

          <TabsContent value="camera" class="mt-0">
            <div v-if="cameras.length" class="grid grid-cols-2 gap-4">
              <button
                v-for="camera in cameras"
                :key="camera.deviceId"
                class="relative aspect-video overflow-hidden rounded-xl border-2 bg-black transition-colors"
                :class="selectedId === camera.deviceId ? 'border-primary' : 'border-border hover:border-primary/60'"
                @click="chooseCamera(camera)"
              >
                <video :ref="el => attachPreview(camera.deviceId, el)" class="size-full -scale-x-100 object-cover" autoplay muted playsinline />
                <div v-if="loadingCameraId === camera.deviceId" class="absolute inset-0 grid place-items-center bg-black/50">
                  <Spinner class="size-6 text-white" />
                </div>
                <span class="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/90 px-3 pb-2 pt-8 text-left text-sm text-white">
                  {{ camera.label || 'Камера' }}
                </span>
              </button>
            </div>
            <div v-else class="grid h-52 place-items-center rounded-xl border border-dashed text-muted-foreground">Внешние камеры не найдены</div>
          </TabsContent>
        </div>
      </Tabs>

      <DialogFooter class="flex-row flex-wrap items-end justify-between gap-4 border-t px-6 py-4 sm:justify-between">
        <div class="flex flex-wrap items-end gap-4 xl:gap-6">
          <div class="space-y-1.5">
            <span class="text-xs font-medium text-muted-foreground">Пресеты</span>
            <div class="flex gap-1">
              <Button
                type="button"
                size="sm"
                :variant="quality === '1440p' && fps === '15' ? 'default' : 'outline'"
                @click="applyPreset('text')"
              >Повышенная чёткость текста</Button>
              <Button
                type="button"
                size="sm"
                :variant="quality === '720p' && fps === '30' ? 'default' : 'outline'"
                @click="applyPreset('motion')"
              >Плавное видео</Button>
            </div>
          </div>
          <div class="space-y-1.5">
            <span class="text-xs font-medium text-muted-foreground">Качество</span>
            <ToggleGroup v-model="quality" type="single" variant="outline" size="sm">
              <ToggleGroupItem v-for="item in qualities" :key="item.value" :value="item.value">{{ item.label }}</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div class="space-y-1.5">
            <span class="text-xs font-medium text-muted-foreground">FPS</span>
            <ToggleGroup v-model="fps" type="single" variant="outline" size="sm">
              <ToggleGroupItem
                  v-for="value in ['5', '15', '30']"
                  :key="value"
                  :value="value"
                  :disabled="quality === '1080p' && value !== '30'"
              >{{ value }}</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <label class="flex h-9 cursor-pointer items-center gap-2 text-sm">
            <Checkbox v-model="includeAudio" />
            <Volume2 class="size-4" />
            Передавать звук с {{ tab === 'application' ? 'приложения' : tab === 'screen' ? 'экрана' : 'камеры' }}
          </label>
        </div>
        <div class="flex gap-2">
          <Button variant="outline" @click="close">Отмена</Button>
          <Button :disabled="!canStart" @click="startSharing">Начать трансляцию</Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
